import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ClearIcon from '@mui/icons-material/Clear';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import HistoryIcon from '@mui/icons-material/History';

import React, { useContext } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { useImmer } from 'use-immer';

import { ApiContext } from '_lib/api/apiContext';
import {
  _email,
  AccountResponseDTO,
  AddressInput,
  Company,
  CompanyCreateApiDTO,
  CompanyUpdateApiDTO,
  ConstantArray,
  isServiceTitanCompany,
  NationalNumberGroups,
  Paths,
  PathValue,
  SupportedTimezoneMap,
  TradeEnum,
  TRADES
} from '_lib/api/apiTypes.shim';
import { AutocompleteFormItem } from '_lib/components/AutocompleteFormItem/AutocompleteFormItem';
import { Button } from '_lib/components/Button/Button';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { CheckboxFormItem } from '_lib/components/CheckboxFormItem/CheckboxFormItem';
import { Chip } from '_lib/components/Chip/Chip';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { FormHelperText } from '_lib/components/FormHelperText/FormHelperText';
import { Grid } from '_lib/components/Grid/Grid';
import { List } from '_lib/components/List/List';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { SelectFormItem } from '_lib/components/SelectFormItem/SelectFormItem';
import { SelectMultipleFormItem } from '_lib/components/SelectMultipleFormItem/SelectMultipleFormItem';
import { Stack } from '_lib/components/Stack/Stack';
import { PhoneNumberTextFieldFormItem, TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { Typography } from '_lib/components/Typography/Typography';
import { BaseAddressForm } from '_lib/featureComponents/FormItems/BaseAddressForm';
import { SearchSelect } from '_lib/featureComponents/SearchSelect/SearchSelect';
import { TradeIcon } from '_lib/featureComponents/Trades/TradesDisplay';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';

import { formatPhone, pickDeeplyNested } from '_lib/utils/helpers';

import { FlexStack } from '../FlexStack/FlexStack';

// Define the specific keys (top-level and nested) that this form controls
export const companyConfigDetailFormValues = [
  'name',
  'nickname',
  'addressLine1',
  'addressLine2',
  'city',
  'stateOrProvince',
  'zipOrPostal',
  'country',
  'callCenter.crossStreets',
  'timezone',
  'phoneNumber',
  'callCenter.vendorPhoneNumber',
  'faxNumber',
  'website',
  'applicantPhoneNumber',
  'applicantWebsite',
  'callCenter.scheduleUrl',
  'callCenter.membershipPlan',
  'callCenter.greetingMessage',
  'callCenter.aiGreetingMessage',
  'callCenter.businessHours',
  'callCenter.ownerName',
  'callCenter.nationalNumberGroupId',
  'callCenter.relatedOrganizations',
  'callCenter.canIncludeFee',
  'callCenter.canWaiveFee',
  'isDispatchEnabled',
  'callCenter.serviceType',
  'callCenter.trades',
  'email',
  'emailSettings.callPilot.ccEmails',
  'emailSettings.callPilot.bccEmails',
  'emailSettings.callPilot.plainTextEmails',
  'sisterCompanies'
] satisfies ConstantArray<Paths<CompanyUpdateApiDTO | CompanyCreateApiDTO>>;

export type CompanyConfigDetailFormValue = (typeof companyConfigDetailFormValues)[number];

// Build the form values type from the array of keys - each key maps to its value type
export type CompanyConfigDetailFormValues = {
  [K in CompanyConfigDetailFormValue]: PathValue<CompanyUpdateApiDTO | CompanyCreateApiDTO, K>;
};

/**
 * Form is only responsible for fields it controls, and we can check for the generic constraint in the
 * props in order for this to be reusable.
 *
 * See https://github.com/orgs/react-hook-form/discussions/8311#discussioncomment-9759698
 */
type CompanyConfigDetailFormProps<T extends CompanyConfigDetailFormValues> = {
  companyConfigDetailFormContext: T extends CompanyConfigDetailFormValues ? UseFormReturn<T> : never;
  account: AccountResponseDTO;
  company?: Company;
  nationalNumberGroups?: NationalNumberGroups | null;
};

export const CompanyConfigDetailForm = <T extends CompanyConfigDetailFormValues>(
  props: CompanyConfigDetailFormProps<T>
) => {
  const { companyConfigDetailFormContext, company, account, nationalNumberGroups } = props;
  const {
    control,
    setValue,
    watch,
    formState: { errors }
  } = companyConfigDetailFormContext;

  const singleColumn = useMediaQuery((theme) => theme.breakpoints.only('xs'));

  const api = useContext(ApiContext);
  const [sisterCompaniesDraft, setSisterCompaniesDraft] = useImmer(company?.sisterCompanies ?? []);

  return (
    <Stack useFlexGap spacing={2} sx={{ width: '100%' }}>
      <FlexCard>
        <PrimaryCardHeader title='General Company Details' />
        <Stack useFlexGap sx={{ padding: 2, width: '100%' }}>
          <FlexStack singleColumn={singleColumn}>
            <TextFieldFormItem
              name='name'
              label='Name'
              control={control}
              required
              fullWidth
              disabled={!!company && !company.isActive}
            />
            <Stack spacing={0} useFlexGap sx={{ width: '100%' }}>
              <TextFieldFormItem
                name='nickname'
                label='Nickname'
                control={control}
                fullWidth
                disabled={!!company && !company.isActive}
              />
              <FormHelperText sx={{ paddingLeft: 1.75, marginTop: -0.5 }}>
                A short name used by CPI to refer to the company
              </FormHelperText>
            </Stack>
          </FlexStack>
          <FlexBox>
            <Grid container spacing={1}>
              <BaseAddressForm
                formContext={companyConfigDetailFormContext as UseFormReturn<AddressInput>}
                isNested={false}
                isEditingExisting={!!company?.id}
                disabled={!!company && !company.isActive}
              />
            </Grid>
          </FlexBox>
          <FlexStack singleColumn={singleColumn}>
            <TextFieldFormItem
              name='callCenter.crossStreets'
              label='Cross Streets'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
            <SelectFormItem
              name='timezone'
              label='Timezone'
              control={control}
              required
              fullWidth
              disabled={!!company && !company.isActive}>
              {Object.entries(SupportedTimezoneMap).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value.displayName}
                </MenuItem>
              ))}
            </SelectFormItem>
          </FlexStack>
          <FlexStack singleColumn={singleColumn}>
            <PhoneNumberTextFieldFormItem
              name='phoneNumber'
              label='Phone Number'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
            <PhoneNumberTextFieldFormItem
              name='callCenter.vendorPhoneNumber'
              label='Vendor Phone Number'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
          </FlexStack>
          <FlexStack singleColumn={singleColumn}>
            <PhoneNumberTextFieldFormItem
              name='faxNumber'
              label='Fax Number'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
            <TextFieldFormItem
              name='website'
              label='Website'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
          </FlexStack>
          <FlexStack singleColumn={singleColumn}>
            <PhoneNumberTextFieldFormItem
              name='applicantPhoneNumber'
              label='Applicant Phone Number'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
            <TextFieldFormItem
              name='applicantWebsite'
              label='Applicant Website'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
          </FlexStack>
        </Stack>
      </FlexCard>
      {!!company && (
        <FlexCard>
          <PrimaryCardHeader title='Sister Companies' />
          <Stack useFlexGap direction='column' spacing={1} sx={{ padding: 2 }}>
            <List disablePadding>
              {sisterCompaniesDraft.map((sisterCompany) => (
                <Chip key={sisterCompany.id} label={sisterCompany.name} />
              ))}
            </List>
            <SearchSelect
              doSearch={async (searchValue) => {
                const res = await api.companies.list({
                  params: {
                    searchWords: searchValue,
                    accountId: account.id,
                    callCenterId: account.callCenterId,
                    serializer: 'full'
                  }
                });
                // Do not include current company, or existing sister companies
                return res.results.filter(
                  (c) => c.id !== company.id && !sisterCompaniesDraft.some((sc) => sc.id === c.id)
                );
              }}
              selectedOption={null}
              trySetSelectedOption={async (option) => {
                if (option) {
                  setSisterCompaniesDraft((existingSisters) => {
                    existingSisters.push(
                      pickDeeplyNested(option, ['id', 'name', 'callCenter.trades']) as (typeof existingSisters)[number]
                    );

                    // If the new company has sisters, push them too
                    option.sisterCompanies.forEach((sc) => {
                      if (!existingSisters.find((existSc) => existSc.id === sc.id) && sc.id !== company.id) {
                        existingSisters.push(sc);
                      }
                    });

                    if (
                      !existingSisters.every((sc) => company.sisterCompanies.find((c) => c.id === sc.id)) ||
                      existingSisters.length !== company.sisterCompanies.length
                    ) {
                      setValue('sisterCompanies', existingSisters);
                    }
                  });
                }
                return false;
              }}
              getSelectedOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
              renderListOption={(company, liProps) => {
                return (
                  <li {...liProps} key={company.id}>
                    <Typography text={company.name} />
                    {company.sisterCompanies.length > 0 && <GroupWorkIcon fontSize='small' />}
                  </li>
                );
              }}
              label='Add Company to Sister Group'
              placeHolder='Company Name'
              sx={{ flexGrow: 1 }}
              issues={
                errors.sisterCompanies
                  ? [{ message: errors.sisterCompanies.message || 'Error', path: ['sisterCompanies'] }]
                  : []
              }
              issuePath='sisterCompanies'
              disabled={!!company && !company.isActive}
            />
            <Stack useFlexGap flexDirection='row'>
              <Button
                disabled={
                  (sisterCompaniesDraft.every((sc) => company.sisterCompanies.find((c) => c.id === sc.id)) &&
                    sisterCompaniesDraft.length === company.sisterCompanies.length) ||
                  (!!company && !company.isActive)
                }
                text='Reset'
                startIcon={<HistoryIcon />}
                onClick={() => {
                  setValue('sisterCompanies', company.sisterCompanies);
                  setSisterCompaniesDraft(company.sisterCompanies ?? []);
                }}
              />
              <Button
                disabled={sisterCompaniesDraft.length === 0 || (!!company && !company.isActive)}
                text={`Remove ${company.name} from Sister Group`}
                startIcon={<ClearIcon />}
                onClick={() => {
                  setValue('sisterCompanies', null);
                  setSisterCompaniesDraft([]);
                }}
              />
            </Stack>
          </Stack>
        </FlexCard>
      )}
      <FlexCard>
        <PrimaryCardHeader title='Business Settings' />
        <Stack useFlexGap sx={{ padding: 2, width: '100%' }}>
          <FlexStack singleColumn={singleColumn}>
            <Stack spacing={0} useFlexGap sx={{ width: '100%' }}>
              <TextFieldFormItem
                name='callCenter.scheduleUrl'
                label='Schedule URL'
                control={control}
                fullWidth
                disabled={!!company && !company.isActive}
              />
              {!!company && isServiceTitanCompany(company) && (
                <FormHelperText sx={{ paddingLeft: 1.75, marginTop: -0.5 }}>
                  Do not set a Schedule URL if call agent should use Service Titan Availability
                </FormHelperText>
              )}
              {!!company && company.isZoned && !company.isScheduleEnabled && (
                <FormHelperText sx={{ paddingLeft: 1.75, marginTop: -0.5 }}>
                  Schedule URL can be overwritten by individual zones
                </FormHelperText>
              )}
            </Stack>
            <TextFieldFormItem
              name='callCenter.membershipPlan'
              label='Membership Plan'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
          </FlexStack>
          <TextFieldFormItem
            name='callCenter.greetingMessage'
            label='Greeting'
            required
            control={control}
            multiline
            maxRows={10}
            minRows={2}
            fullWidth
            disabled={!!company && !company.isActive}
          />
          <TextFieldFormItem
            name='callCenter.aiGreetingMessage'
            label={
              <Stack direction='row' useFlexGap>
                <AutoAwesomeIcon fontSize='small' />
                {'CPI Greeting'}
              </Stack>
            }
            required
            control={control}
            multiline
            maxRows={10}
            minRows={2}
            fullWidth
            sx={{ '& .MuiFormLabel-root': { display: 'flex' } }}
            disabled={!!company && !company.isActive}
          />
          <TextFieldFormItem
            name='callCenter.businessHours'
            label='Business Hours'
            control={control}
            multiline
            maxRows={10}
            minRows={2}
            fullWidth
            disabled={!!company && !company.isActive}
          />
          <TextFieldFormItem
            name='callCenter.relatedOrganizations'
            label='Related Organizations'
            control={control}
            multiline
            maxRows={10}
            minRows={2}
            fullWidth
            disabled={!!company && !company.isActive}
          />
          <TextFieldFormItem
            name='callCenter.ownerName'
            label='Owner/GM'
            control={control}
            fullWidth
            disabled={!!company && !company.isActive}
          />
          <FlexStack singleColumn={singleColumn}>
            <TextFieldFormItem
              name='callCenter.serviceType'
              label='Service Type'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
            <SelectMultipleFormItem
              name='callCenter.trades'
              label='Trades'
              control={control}
              required
              disabled={!!company && !company.isActive}
              renderValue={(values) => {
                if (values && Array.isArray(values) && values.length) {
                  return (
                    <Stack direction='row'>
                      {values.map((tr, index) => (
                        <React.Fragment key={tr}>
                          <Stack direction='row' spacing={0.5} alignItems='center'>
                            <Typography>{TradeEnum[tr]}</Typography>
                            <TradeIcon trade={tr} />
                          </Stack>
                          {index < values.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                    </Stack>
                  );
                }
                return 'Trades';
              }}>
              {TRADES.map((trade) => (
                <MenuItem key={trade} value={trade}>
                  <Checkbox checked={(watch('callCenter.trades') as number[])?.includes(trade)} />
                  <ListItemText
                    primary={
                      <Stack direction='row'>
                        <Typography>{TradeEnum[trade]}</Typography>
                        <TradeIcon trade={trade} />
                      </Stack>
                    }
                  />
                </MenuItem>
              ))}
            </SelectMultipleFormItem>
          </FlexStack>
          <FlexStack singleColumn={singleColumn} sx={{ alignContent: 'center' }}>
            <CheckboxFormItem
              name='callCenter.canIncludeFee'
              label='Appointment fee can be included with work'
              control={control}
              disabled={!!company && !company.isActive}
            />
            <CheckboxFormItem
              name='callCenter.canWaiveFee'
              label='Appointment fee can be waived'
              control={control}
              disabled={!!company && !company.isActive}
            />
            <CheckboxFormItem
              name='isDispatchEnabled'
              label='Dispatch Enabled'
              control={control}
              disabled={!!company && !company.isActive}
            />
          </FlexStack>
          <SelectFormItem
            name='callCenter.nationalNumberGroupId'
            label='National Numbers'
            control={control}
            fullWidth
            disabled={!!company && !company.isActive}>
            {nationalNumberGroups &&
              nationalNumberGroups.length > 0 &&
              nationalNumberGroups.map((group) => (
                <MenuItem
                  key={group.id}
                  value={group.id}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant='subtitle1' text={group.name} />
                  <List
                    sx={{
                      width: '100%'
                    }}
                    disablePadding>
                    {group.nationalNumbers.map((nn, idx) => (
                      <ListItem key={idx} disableGutters sx={{ paddingY: 0.5 }}>
                        <Stack useFlexGap direction='row' spacing={1} sx={{ alignItems: 'center' }}>
                          <TradeIcon trade={nn.trade} color='primary' />
                          <Typography text={formatPhone(nn.phoneNumber)} />
                          <Typography text='|' />
                          <Typography text={nn.name} />
                        </Stack>
                      </ListItem>
                    ))}
                  </List>
                </MenuItem>
              ))}
            <MenuItem value={''}>None</MenuItem>
          </SelectFormItem>
        </Stack>
      </FlexCard>
      <FlexCard>
        <PrimaryCardHeader title='Email Settings' />
        <Stack useFlexGap sx={{ padding: 2, width: '100%' }}>
          <TextFieldFormItem
            name='email'
            label='Email'
            control={control}
            fullWidth
            disabled={!!company && !company.isActive}
          />
          <AutocompleteFormItem
            control={control}
            splitStringDelimiter=','
            name='emailSettings.callPilot.ccEmails'
            label='CC Email Addresses'
            placeholder='CC Email Addresses'
            fullWidth
            inputMode='email'
            disabled={!!company && !company.isActive}
            onChange={(_e, value) => {
              const validEmails = (value as string[]).filter((email) => _email().safeParse(email).success);
              setValue('emailSettings.callPilot.ccEmails', validEmails.join(',') as never);
            }}
          />
          <AutocompleteFormItem
            control={control}
            splitStringDelimiter=','
            name='emailSettings.callPilot.bccEmails'
            label='BCC Email Addresses'
            placeholder='BCC Email Addresses'
            fullWidth
            inputMode='email'
            disabled={!!company && !company.isActive}
            onChange={(_e, value) => {
              const validEmails = (value as string[]).filter((email) => _email().safeParse(email).success);
              setValue('emailSettings.callPilot.bccEmails', validEmails.join(',') as never);
            }}
          />
          <AutocompleteFormItem
            control={control}
            splitStringDelimiter=','
            name='emailSettings.callPilot.plainTextEmails'
            label='Plain Text Email Addresses'
            placeholder='Plain Text Email Addresses'
            fullWidth
            inputMode='email'
            disabled={!!company && !company.isActive}
            onChange={(_e, value) => {
              const validEmails = (value as string[]).filter((email) => _email().safeParse(email).success);
              setValue('emailSettings.callPilot.plainTextEmails', validEmails.join(',') as never);
            }}
          />
        </Stack>
      </FlexCard>
    </Stack>
  );
};
